/* =========================================================================
 * Death Row — brand collection template (templates/collection.deathrow.json)
 * -------------------------------------------------------------------------
 * - Autoplay videos (hero, casket): load when near the viewport, play while
 *   visible, "once" holds the last frame. Desktop/mobile sources.
 * - Inmate stage: one pinned stage holds every cell. Scroll position maps to
 *   a global cell position g (0 … cells); each cell film is scrubbed frame by
 *   frame by its own share of it, forwards and backwards (a short video, as on
 *   deathrowpouches.com; an image sequence on a canvas is the alternative),
 *   the next film dissolves in over the current one around each cut (with a
 *   short CCTV static), the file card fades in mid-film, HUD timecode + meter,
 *   the rail follows the active cell.
 * - Reveal-on-scroll, CCTV clock, casket pause toggle.
 * - prefers-reduced-motion / Save-Data: no autoplay, no scrub, stills only.
 * No dependencies. Re-initialises on theme editor section loads.
 * ========================================================================= */
(function () {
  'use strict';

  if (window.__DR_INIT__) return;
  window.__DR_INIT__ = true;

  document.documentElement.classList.add('dr-js');

  var reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileMq = window.matchMedia('(max-width: 749px)');
  var saveData = !!(navigator.connection && navigator.connection.saveData);

  /* share of a cell's scroll used for the dissolve into the next cell, on each side of the cut */
  var CUT = 0.11;

  function still() {
    return reduceMq.matches || saveData;
  }

  function clamp(n, a, b) {
    return n < a ? a : n > b ? b : n;
  }

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function timecode(sec) {
    sec = Math.max(0, sec || 0);
    var s = Math.floor(sec);
    var f = Math.floor((sec - s) * 24);
    return '00:' + pad(Math.floor(s / 60)) + ':' + pad(s % 60) + ':' + pad(f);
  }

  /* ------------------------------------------------------------- videos */
  function pickSrc(v) {
    return (mobileMq.matches && v.getAttribute('data-src-mobile')) || v.getAttribute('data-src') || '';
  }

  /* Stream the film. If that fails (Safari refuses to stream from a server that ignores byte
     ranges, theme dev's local proxy is one) or never starts, fetch it whole and play it from memory. */
  function loadVideo(v) {
    if (!v || v.__drLoaded) return;
    var src = pickSrc(v);
    if (!src) return;
    v.__drLoaded = true;
    v.preload = 'auto';
    var settled = false;
    var timer = 0;
    var fallback;
    var settle = function () {
      settled = true;
      window.clearTimeout(timer);
      v.removeEventListener('error', fallback);
    };
    fallback = function () {
      if (settled) return;
      settle();
      if (!window.fetch || !window.Blob || !window.URL || !URL.createObjectURL) return;
      window
        .fetch(src, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.arrayBuffer();
        })
        .then(function (buf) {
          /* typed: Safari will not play an untyped media blob */
          v.src = URL.createObjectURL(new Blob([buf], { type: 'video/mp4' }));
          v.load();
          if (v.__drInView && !v.__drUserPaused) safePlay(v);
        })
        .catch(function () {});
    };
    v.addEventListener('error', fallback);
    v.addEventListener('loadeddata', settle, { once: true });
    timer = window.setTimeout(fallback, 8000);
    v.src = src;
    v.load();
  }

  function safePlay(v) {
    var p = v.play && v.play();
    if (p && p.catch) p.catch(function () {});
    return p;
  }

  function initAutoplay(root) {
    root.querySelectorAll('video[data-dr-autoplay]').forEach(function (v) {
      if (v.__drAuto) return;
      v.__drAuto = true;

      var media = v.closest('[data-dr-media]') || v.parentNode;
      var once = v.getAttribute('data-dr-autoplay') === 'once';
      v.muted = true;
      v.playsInline = true;
      v.loop = !once;

      if (still()) return;

      v.addEventListener('playing', function () {
        media.classList.add('is-playing');
      });
      /* the box can follow the film's own shape (the phone hero does: --dr-media-ratio in deathrow.css) */
      v.addEventListener('loadedmetadata', function () {
        if (v.videoWidth && v.videoHeight) media.style.setProperty('--dr-media-ratio', v.videoWidth + ' / ' + v.videoHeight);
      });
      v.addEventListener('ended', function () {
        media.classList.add('is-ended');
      });

      if (v.getAttribute('src') || v.querySelector('source')) v.__drLoaded = true;
      /* above the fold (the hero): load at once instead of waiting for the observer */
      if (v.hasAttribute('data-dr-eager')) loadVideo(v);

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          v.__drInView = e.isIntersecting;
          if (e.isIntersecting) {
            loadVideo(v);
            if (!(once && v.ended) && !v.__drUserPaused) safePlay(v);
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { rootMargin: '30% 0px' });
      io.observe(v);
    });

    root.querySelectorAll('[data-dr-toggle]').forEach(function (btn) {
      if (btn.__dr) return;
      btn.__dr = true;
      var v = document.getElementById(btn.getAttribute('data-dr-toggle'));
      if (!v) return;
      if (still()) {
        btn.hidden = true;
        return;
      }
      btn.addEventListener('click', function () {
        if (v.paused) {
          v.__drUserPaused = false;
          safePlay(v);
        } else {
          v.__drUserPaused = true;
          v.pause();
        }
      });
      var sync = function () {
        var playing = !v.paused;
        btn.setAttribute('aria-pressed', playing ? 'false' : 'true');
        btn.querySelector('[data-dr-toggle-label]').textContent = playing ? btn.getAttribute('data-pause') : btn.getAttribute('data-play');
      };
      v.addEventListener('play', sync);
      v.addEventListener('pause', sync);
    });
  }

  /* ---------------------------------------------------------- inmate stage */
  var stages = [];
  var stageIO = null;
  var ticking = false;

  function requestTick() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(frame);
    }
  }

  /* ------------------------------------------------- cell films (video, the default)
   * Done the way deathrowpouches.com does it: the film's currentTime follows the scroll position
   * directly, every frame of the clip and with no easing, so the picture is exactly where the
   * scroll is, forwards and backwards. The clips are short H.264 files (1280×720, 24 fps, a
   * keyframe every ~7 frames), so a seek either way only decodes a few frames.
   * What keeps the scrub smooth:
   * - a film is only scrubbed once all of it is in memory: it is fetched whole and played from a
   *   blob URL (a seek into a part that is still downloading stalls the picture);
   * - one seek at a time: while the decoder is busy the newest position waits and goes in on
   *   'seeked', with a watchdog in case that event never comes (iOS sometimes drops it);
   * - iOS only paints seeks once the decoder has been primed by a muted play().
   * Cell 1 is fetched right after page load, the others two cells ahead; cells far behind let go
   * of their film (phones only keep a few video decoders). */
  var SEEK_WATCHDOG = 300;

  function seek(sc) {
    var v = sc.v;
    if (!v || !sc.ready || !sc.dur) return;
    if (v.seeking && !sc.stuck) {
      sc.pending = true;
      return;
    }
    var t = Math.min(sc.dur * 0.995, Math.max(0, sc.p * sc.dur));
    var force = sc.stuck;
    sc.pending = false;
    sc.stuck = false;
    if (!force && Math.abs(v.currentTime - t) < 0.01) return;
    v.currentTime = t;
    window.clearTimeout(sc.watchdog);
    sc.watchdog = window.setTimeout(function () {
      if (v.seeking) {
        sc.stuck = true;
        seek(sc);
      }
    }, SEEK_WATCHDOG);
  }

  function markReady(sc) {
    if (sc.ready || !sc.loading) return;
    sc.ready = true;
    sc.el.classList.add('is-ready');
    seek(sc);
  }

  function setupScene(sc) {
    var v = sc.v;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    v.addEventListener('loadedmetadata', function () {
      sc.dur = v.duration || 0;
    });
    v.addEventListener('loadeddata', function () {
      if (!sc.loading) return;
      sc.dur = v.duration || sc.dur;
      /* iOS only paints seeks after the decoder has been primed by a (muted) play */
      var p = v.play();
      if (p && p.then) {
        p.then(function () {
          v.pause();
          markReady(sc);
        }).catch(function () {
          markReady(sc);
        });
      } else {
        v.pause();
        markReady(sc);
      }
    });
    v.addEventListener('seeked', function () {
      window.clearTimeout(sc.watchdog);
      sc.stuck = false;
      if (sc.pending) seek(sc);
    });
  }

  /* fetch the whole film, then play it from memory (falls back to streaming it) */
  function loadFilm(sc) {
    var v = sc.v;
    var src = v && pickSrc(v);
    if (!src || sc.loading) return;
    sc.loading = true;
    var gen = (sc.gen = (sc.gen || 0) + 1);
    var use = function (url) {
      if (gen !== sc.gen) return;
      v.preload = 'auto';
      v.src = url;
      v.load();
    };
    if (!window.fetch || !window.Blob || !window.URL || !URL.createObjectURL) return use(src);
    window
      .fetch(src, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.arrayBuffer();
      })
      .then(function (buf) {
        if (gen !== sc.gen) return;
        /* typed: Safari will not play an untyped media blob */
        sc.blob = URL.createObjectURL(new Blob([buf], { type: 'video/mp4' }));
        use(sc.blob);
      })
      .catch(function () {
        use(src);
      });
  }

  function releaseFilm(sc) {
    if (!sc.loading) return;
    sc.loading = false;
    sc.gen = (sc.gen || 0) + 1;
    sc.ready = false;
    sc.pending = false;
    sc.stuck = false;
    window.clearTimeout(sc.watchdog);
    sc.el.classList.remove('is-ready');
    if (sc.v) {
      sc.v.removeAttribute('src');
      sc.v.load();
    }
    if (sc.blob) {
      URL.revokeObjectURL(sc.blob);
      sc.blob = null;
    }
  }

  /* ------------------------------------------------- image sequences (the alternative)
   * A cell film as numbered frames (theme assets) drawn on a canvas by scroll position. Fewer
   * frames than the film, so the stage glides toward the scroll position to blend between them.
   * Smoothness going forward comes from never meeting a cold frame:
   * - the frames just ahead of the playhead (in the scroll direction) are fetched first;
   * - every frame is decoded (img.decode, off the main thread) before it may be drawn;
   * - loading starts shortly after page load and runs two cells ahead;
   * - a missing frame never makes the picture jump: it dissolves between the nearest loaded
   *   frames on either side, and loaded neighbours are blended by the fractional position.
   * Cells far behind let go of their frames. */
  var SEQ_PARALLEL = 6;
  var SEQ_AHEAD = 16;
  var SEQ_STRIDES = [4, 2, 1];

  function pickFrame(sc) {
    var n = sc.seq.length;
    var pos = clamp(Math.round(sc.want || 0), 0, n - 1);
    var dir = sc.dir || 1;
    var k;
    var d;
    var free = function (i) {
      return i >= 0 && i < n && !sc.frames[i] && !sc.flight[i] && !sc.failed[i];
    };
    for (d = 0; d <= SEQ_AHEAD; d++) {
      k = pos + dir * d;
      if (free(k)) return k;
    }
    for (d = 1; d <= 4; d++) {
      k = pos - dir * d;
      if (free(k)) return k;
    }
    for (var s = 0; s < SEQ_STRIDES.length; s++) {
      for (k = 0; k < n; k += SEQ_STRIDES[s]) {
        if (free(k)) return k;
      }
    }
    return -1;
  }

  function loadSeq(sc) {
    if (!sc.seq || sc.seqLoading) return;
    sc.seqLoading = true;
    sc.frames = sc.frames || [];
    sc.flight = [];
    sc.failed = [];
    var gen = (sc.seqGen = (sc.seqGen || 0) + 1);

    var next = function () {
      if (gen !== sc.seqGen) return;
      var k = pickFrame(sc);
      if (k < 0) return;
      sc.flight[k] = true;
      var img = new Image();
      img.decoding = 'async';
      var ready = function () {
        if (gen !== sc.seqGen) return;
        sc.flight[k] = false;
        sc.frames[k] = img;
        sc.dirty = true;
        requestTick();
        next();
      };
      img.onload = function () {
        /* decode now, off the main thread, so the first draw of this frame never stalls.
           Browsers hold decode() back in a hidden tab: a short timer makes sure it never waits. */
        if (!img.decode) return ready();
        var settled = false;
        var once = function () {
          if (settled) return;
          settled = true;
          ready();
        };
        img.decode().then(once, once);
        window.setTimeout(once, 300);
      };
      img.onerror = function () {
        if (gen !== sc.seqGen) return;
        sc.flight[k] = false;
        sc.failed[k] = true;
        next();
      };
      img.src = sc.seq[k];
    };
    for (var i = 0; i < SEQ_PARALLEL; i++) next();
  }

  function releaseSeq(sc) {
    if (!sc.seqLoading) return;
    sc.seqLoading = false;
    sc.seqGen = (sc.seqGen || 0) + 1;
    sc.frames = [];
    sc.drawKey = '';
    sc.frame = -1;
  }

  function sizeCanvas(sc) {
    var r = sc.el.getBoundingClientRect();
    /* the frames are 1280 wide: past 1× on desktop only costs fill rate, phones crop and enlarge */
    var dpr = Math.min(window.devicePixelRatio || 1, mobileMq.matches ? 1.5 : 1);
    var w = Math.max(1, Math.round(r.width * dpr));
    var h = Math.max(1, Math.round(r.height * dpr));
    if (sc.canvas.width !== w || sc.canvas.height !== h) {
      sc.canvas.width = w;
      sc.canvas.height = h;
    }
    sc.sized = true;
    sc.dirty = true;
  }

  function paint(ctx, img, cw, ch, fx, alpha) {
    var iw = img.naturalWidth;
    var ih = img.naturalHeight;
    if (!iw || !ih) return;
    var s = Math.max(cw / iw, ch / ih);
    var dw = iw * s;
    var dh = ih * s;
    ctx.globalAlpha = alpha;
    ctx.drawImage(img, (cw - dw) * fx, (ch - dh) * 0.5, dw, dh);
  }

  function drawSeq(sc, p) {
    if (!sc.canvas) return;
    if (!sc.sized) sizeCanvas(sc);
    var n = sc.seq.length;
    var f = p * (n - 1);
    var lo = -1;
    var hi = -1;
    var k;
    for (k = Math.floor(f); k >= 0; k--) {
      if (sc.frames[k]) {
        lo = k;
        break;
      }
    }
    for (k = Math.ceil(f); k < n; k++) {
      if (sc.frames[k]) {
        hi = k;
        break;
      }
    }
    if (lo < 0 && hi < 0) return;
    if (lo < 0) lo = hi;
    if (hi < 0) hi = lo;
    var t = hi > lo ? clamp((f - lo) / (hi - lo), 0, 1) : 0;
    var key = lo + ':' + hi + ':' + t.toFixed(3);
    if (!sc.dirty && key === sc.drawKey) return;

    var ctx = sc.ctx || (sc.ctx = sc.canvas.getContext('2d'));
    var cw = sc.canvas.width;
    var ch = sc.canvas.height;
    /* the inmate's position steers the crop on phones, like the CSS object-position */
    var fx = mobileMq.matches ? sc.fx : 0.5;
    paint(ctx, sc.frames[lo], cw, ch, fx, 1);
    if (t > 0.004 && hi !== lo) paint(ctx, sc.frames[hi], cw, ch, fx, t);
    ctx.globalAlpha = 1;

    sc.drawKey = key;
    sc.frame = lo + (hi - lo) * t;
    sc.dirty = false;
    if (!sc.ready) {
      sc.ready = true;
      sc.el.classList.add('is-ready');
    }
  }

  /* the file card: in over 30–42 % of the film, out over 80–90 % (before the next cut) */
  function cardOpacity(p) {
    if (p < 0.3) return 0;
    if (p < 0.42) return (p - 0.3) / 0.12;
    if (p < 0.8) return 1;
    return Math.max(0, 1 - (p - 0.8) / 0.1);
  }

  /* write a style value only when it changed: no needless style work per frame */
  function put(sc, name, value) {
    if (sc.css[name] === value) return;
    sc.css[name] = value;
    if (name === 'opacity') sc.el.style.opacity = value;
    else sc.el.style.setProperty(name, value);
  }

  function renderStage(st) {
    var g = st.g;
    var n = st.scenes.length;
    var dir = g >= st.lastG ? 1 : -1;
    st.lastG = g;
    /* the camera on air switches right on the cut, under the static */
    var cam = clamp(Math.floor(g), 0, n - 1);

    for (var i = 0; i < n; i++) {
      var sc = st.scenes[i];
      var p = clamp(g - i, 0, 1);
      /* this cell dissolves in over the previous one around its cut … */
      var a = i === 0 ? 1 : clamp((g - (i - CUT)) / (2 * CUT), 0, 1);
      /* … and is hidden once the next one has fully covered it */
      var covered = i < n - 1 && g >= i + 1 + CUT;
      var on = a > 0 && !covered;
      var o = on ? cardOpacity(p) : 0;

      if (sc.on !== on) {
        sc.on = on;
        sc.el.classList.toggle('is-on', on);
      }
      put(sc, 'opacity', a.toFixed(3));
      put(sc, '--p', p.toFixed(3));
      put(sc, '--o', o.toFixed(2));
      var live = o > 0.6;
      if (sc.live !== live) {
        sc.live = live;
        sc.el.classList.toggle('is-live', live);
      }
      if (sc.cam !== (i === cam)) {
        sc.cam = i === cam;
        sc.el.classList.toggle('is-cam', sc.cam);
      }
      sc.p = p;
      if (sc.tc && on) {
        var tc = timecode(p * (sc.dur || 8));
        if (sc.tcText !== tc) {
          sc.tcText = tc;
          sc.tc.textContent = tc;
        }
      }

      if (sc.seq) {
        /* frames for this cell and the next two load ahead; cells far behind let go */
        sc.want = p * (sc.seq.length - 1);
        sc.dir = dir;
        if (g > i - 1.6 && g < i + 2.2) loadSeq(sc);
        else if (g >= i + 2.2) releaseSeq(sc);
        if (on) drawSeq(sc, p);
      } else if (sc.v) {
        /* the film of this cell and of the next two is fetched ahead; cells far behind let go */
        if (g > i - 2 && g < i + 2.2) loadFilm(sc);
        else if (g >= i + 2.2) releaseFilm(sc);
        if (on) seek(sc);
      }
    }

    /* CCTV static, peaking on each cut (its layer only exists while it shows) */
    var x = 0;
    for (var b = 1; b < n; b++) {
      var d = Math.abs(g - b);
      if (d < CUT) x = Math.max(x, 1 - d / CUT);
    }
    if (st.static) {
      var so = (x * 0.42).toFixed(3);
      if (st.staticOpacity !== so) {
        st.staticOpacity = so;
        st.static.style.opacity = so;
        st.el.classList.toggle('is-cutting', x > 0);
      }
    }

    /* the last stretch: the casket rises over the stage, the skip link and rail step aside */
    var end = g > n - 0.3;
    if (end !== st.end) {
      st.end = end;
      st.el.classList.toggle('is-end', end);
    }

    /* rail: the cell that is (mostly) on screen */
    var active = clamp(Math.floor(g + CUT), 0, n - 1);
    if (active !== st.active) {
      st.active = active;
      st.rail.forEach(function (link, k) {
        if (k === active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }
  }

  var lastFrameAt = 0;

  function frame(now) {
    ticking = false;
    now = now || (window.performance ? performance.now() : Date.now());
    /* films follow the scroll exactly, like a scrubbed timeline; image sequences glide toward it
       (time-based easing: the same glide at 60 Hz and 120 Hz) */
    var dt = lastFrameAt ? Math.min(64, now - lastFrameAt) : 16.7;
    lastFrameAt = now;
    var ease = 1 - Math.pow(0.8, dt / 16.7);
    var again = false;

    for (var s = 0; s < stages.length; s++) {
      var st = stages[s];
      if (!st.near || st.still) continue;
      var r = st.el.getBoundingClientRect();
      /* the pin sticks for exactly (stage height − pin height) of scroll. Not the window height:
         on phones that grows and shrinks with the browser bar, which would move the films */
      var span = Math.max(1, r.height - st.pin.offsetHeight);
      var target = clamp(-r.top / span, 0, 1) * st.scenes.length;
      if (st.glide) {
        var d = target - st.g;
        st.g = Math.abs(d) < 0.0015 ? target : st.g + d * ease;
        if (st.g !== target) again = true;
      } else {
        st.g = target;
      }
      renderStage(st);
    }

    if (again) requestTick();
    else lastFrameAt = 0;
  }

  function startStage(st) {
    if (st.started || st.still || !st.scenes[0]) return;
    st.started = true;
    if (st.scenes[0].seq) loadSeq(st.scenes[0]);
    else loadFilm(st.scenes[0]);
  }

  function onStageIO(entries) {
    entries.forEach(function (e) {
      var st = e.target.__dr;
      if (!st) return;
      st.near = e.isIntersecting;
      if (e.isIntersecting) startStage(st);
    });
    requestTick();
  }

  function initStages(root) {
    root.querySelectorAll('[data-dr-stage]').forEach(function (el) {
      if (el.__dr) return;
      var st = {
        el: el,
        pin: el.querySelector('.dr-stage__pin'),
        scenes: [],
        rail: Array.prototype.slice.call(el.querySelectorAll('[data-dr-rail] a')),
        static: el.querySelector('.dr-stage__static'),
        g: 0,
        lastG: 0,
        active: 0,
        near: false,
        started: false,
        still: still()
      };
      el.querySelectorAll('[data-dr-scene]').forEach(function (sEl) {
        var seq = null;
        var seqEl = sEl.querySelector('[data-dr-seq]');
        if (seqEl) {
          try {
            seq = JSON.parse(seqEl.textContent);
          } catch (err) {
            seq = null;
          }
          if (!seq || seq.length < 2) seq = null;
        }
        st.scenes.push({
          el: sEl,
          v: sEl.querySelector('video'),
          tc: sEl.querySelector('[data-dr-tc]'),
          canvas: sEl.querySelector('.dr-scene__canvas'),
          seq: seq,
          fx: (parseFloat(sEl.style.getPropertyValue('--dr-fx')) || 50) / 100,
          frames: [],
          flight: [],
          failed: [],
          css: {},
          frame: -1,
          p: 0,
          dur: 0,
          on: null,
          ready: false,
          loading: false,
          pending: false,
          stuck: false
        });
      });
      st.glide = st.scenes.some(function (sc) {
        return !!sc.seq;
      });
      el.__dr = st;
      stages.push(st);

      if (st.still) {
        el.classList.add('is-still');
        st.scenes.forEach(function (sc) {
          sc.el.classList.add('is-on', 'is-live', 'is-cam');
          sc.el.style.setProperty('--o', '1');
          sc.el.style.setProperty('--p', '1');
        });
        return;
      }

      el.classList.add('is-stage');
      st.scenes.forEach(setupScene);
      /* start fetching the cell films about one screen ahead, not on the first screen (Lowe 8 Oct: lighter page) */
      if (!stageIO) stageIO = new IntersectionObserver(onStageIO, { rootMargin: '110% 0px' });
      stageIO.observe(el);
      /* warm the first cell up as soon as the page has settled, long before anyone scrolls there */
      var warm = function () {
        window.setTimeout(function () {
          startStage(st);
        }, 600);
      };
      if (document.readyState === 'complete') warm();
      else window.addEventListener('load', warm, { once: true });
    });
    requestTick();
  }

  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', function () {
    stages.forEach(function (st) {
      st.scenes.forEach(function (sc) {
        sc.sized = false;
      });
    });
    requestTick();
  });

  /* ------------------------------------------------------------- reveal */
  function initReveal(root) {
    var els = root.querySelectorAll('[data-dr-reveal]:not(.is-in)');
    if (!els.length) return;
    if (still() || !('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
      els.forEach(function (e) {
        e.classList.add('is-in');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -4% 0px' });
    var vh = window.innerHeight;
    els.forEach(function (e) {
      /* already on screen at load (e.g. the hero): show at once, staggered by --i */
      var r = e.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) e.classList.add('is-in');
      else io.observe(e);
    });
  }

  /* -------------------------------------------------------- header menu
   * On a Death Row page the store menu gives way to a link back to the store and links to the
   * page's own sections. The hero renders them in a <template>; this swaps them into the theme
   * header: the desktop menu and the phone's menu drawer. Without JS the store menu stays. */
  function initNav() {
    var tpl = document.querySelector('template[data-dr-nav]');
    if (!tpl) return;
    var desk = tpl.content.querySelector('[data-dr-nav-desktop]');
    var list = tpl.content.querySelector('[data-dr-nav-drawer]');

    document.querySelectorAll('.header-section .header__menu').forEach(function (menu, k) {
      if (menu.__drNav) return;
      menu.__drNav = true;
      menu.innerHTML = '';
      /* one menu only (a split header has two) */
      if (k === 0 && desk) menu.appendChild(desk.cloneNode(true));
    });

    var drawer = document.getElementById('MenuDrawer');
    var dm = drawer && drawer.querySelector('.drawer__menu');
    if (dm && list && !dm.__drNav) {
      dm.__drNav = true;
      dm.innerHTML = list.innerHTML;
      dm.addEventListener('click', function (e) {
        var a = e.target.closest('a[data-dr-nav-link]');
        var href = a && a.getAttribute('href');
        if (!href || href.charAt(0) !== '#') return;
        var target = document.getElementById(href.slice(1));
        if (!target) return;
        e.preventDefault();
        var close = drawer.querySelector('.drawer__close');
        if (close) close.click();
        /* the open drawer locks the page: scroll once it has let go */
        window.setTimeout(function () {
          target.scrollIntoView();
          if (window.history && history.replaceState) history.replaceState(null, '', href);
        }, 420);
      });
    }
  }

  /* --------------------------------------------------------- CCTV clock */
  var clockTimer = null;

  function tickClock() {
    var els = document.querySelectorAll('[data-dr-clock]');
    if (!els.length) return;
    var d = new Date();
    var txt = pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear() + '  ' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds());
    els.forEach(function (el) {
      el.textContent = txt;
    });
  }

  function initClock() {
    tickClock();
    if (!clockTimer) clockTimer = window.setInterval(tickClock, 1000);
  }

  /* --------------------------------------------------------------- boot */
  function init(root) {
    initNav();
    initAutoplay(root);
    initStages(root);
    initReveal(root);
    initClock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    stages = stages.filter(function (st) {
      if (e.target.contains(st.el)) {
        if (stageIO) stageIO.unobserve(st.el);
        st.scenes.forEach(releaseFilm);
        return false;
      }
      return true;
    });
  });
})();
